import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { AuthResponse } from './types';
import UserAuth from './auth';
import { NAV_ROUTES } from '@constants/navRoutes';
import { prepareAndNavigateForAuthentication } from '@utils/common';

const MAX_RETRIES = 1;

// Utility function to prepare auth headers
const prepareAuthHeaders = (headers: Headers) => {
  const token = UserAuth.accessToken;
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
};

const authQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_KEYCLOAK_V2_BASE_URL}/realms/${process.env.REACT_APP_KEYCLOAK_REALM}/protocol/openid-connect`,
});

const noAuthBaseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_API_DOMAIN}`,
});

// Main query with retry and re-authentication
const baseQueryWithRetryAndReAuth = async (args: any, api: any, extraOptions: any) => {
  const isMutation = api.type === 'mutation';
  let retries = 0;
  let reAuthAttempts = 0;

  if (!isMutation) {
    let detailedErrorObject = {
      code: null as any,
      message: null as any,
      data: null as any,
      status: null as any,
    };
    // Retry logic for queries
    while (retries < MAX_RETRIES) {
      try {
        const result = await fetchBaseQuery({
          baseUrl: `${process.env.REACT_APP_API_DOMAIN}`,
          prepareHeaders: prepareAuthHeaders,
        })(args, api, extraOptions);

        if (result.error) {
          // Handle Keycloak user missing in BE error
          if (result.error.status === 401 && (result.error.data as any)?.code === 4001) {
            throw new Error('User not found');
          }

          // Handle Keycloak token expiry
          if (result.error.status === 401 && UserAuth.refreshToken) {
            reAuthAttempts++;

            // Check if re-authentication attempts exceeded
            if (reAuthAttempts > MAX_RETRIES) {
              console.error('Re-authentication limit exceeded. Redirecting to /auth...');
              throw new Error('Re-authentication failed');
            }

            console.warn(
              `Token expired. Attempting re-authentication (${reAuthAttempts}/${MAX_RETRIES})...`,
            );

            // Attempt re-authentication with refresh token
            const reAuthResult = await api
              .dispatch(
                reAuthApi.endpoints.authenticate.initiate({
                  refresh_token: UserAuth.refreshToken,
                }),
              )
              .unwrap();

            if (reAuthResult.access_token) {
              console.warn(
                '%cRe-authentication successful. Retrying original request...',
                'background-color: #06402B; color: white; padding: 5px; border-radius: 5px',
              );
              const { access_token, refresh_token } = reAuthResult;
              UserAuth.setTokens({ access_token, refresh_token });

              // Retry original request with new token
              continue;
            } else {
              throw new Error('Re-authentication failed');
            }
          }

          detailedErrorObject = {
            code: (result.error.data as any)?.code || null,
            message: (result.error.data as any)?.message || null,
            data: (result.error.data as any)?.data || null,
            status: result.error.status ?? null,
          };
          throw new Error((result.error.data as any)?.message || 'API request error occurred');
        }

        // Return the result if successful
        return result;
      } catch (err) {
        retries++;

        // User not found in backend
        if ((err as Error).message === 'User not found') {
          console.error('User not found');
          window.location.href = NAV_ROUTES.SIGNUP;
          return;
        }

        //Re-authentication error from keyclock
        if ((err as any)?.data?.error === 'Re-authentication failed') {
          prepareAndNavigateForAuthentication();
          return;
        }

        if (retries >= MAX_RETRIES) {
          console.error(`Retry limit reached. Error: ${JSON.stringify(err as any)}`);

          // Handle redirect based on error type
          if ((err as any)?.data?.error === 'invalid_grant') {
            prepareAndNavigateForAuthentication();
          }

          return {
            error: {
              message: 'Failed after maximum retries',
              error: err,
              detailedErrorObject: detailedErrorObject || undefined,
            },
          };
        }
      }
    }
  }

  // If it's a mutation, attempt without retries
  if (isMutation) {
    let detailedErrorObject = {
      code: null as any,
      message: null as any,
      data: null as any,
      status: null as any,
    };
    try {
      const result = await fetchBaseQuery({
        baseUrl: `${process.env.REACT_APP_API_DOMAIN}`,
        prepareHeaders: prepareAuthHeaders,
      })(args, api, extraOptions);

      if (result.error) {
        if (result.error.status === 401 && (result.error.data as any)?.code === 4001) {
          throw new Error('USER_NOT_FOUND');
        }
        if (result.error.status === 401 && UserAuth.refreshToken) {
          // Attempt re-authentication for mutations
          const reAuthResult = await api
            .dispatch(
              reAuthApi.endpoints.authenticate.initiate({
                refresh_token: UserAuth.refreshToken,
              }),
            )
            .unwrap();

          if (reAuthResult.access_token) {
            const { access_token, refresh_token } = reAuthResult;
            UserAuth.setTokens({ access_token, refresh_token });

            // Retry the mutation with new token
            return await fetchBaseQuery({
              baseUrl: `${process.env.REACT_APP_API_DOMAIN}`,
              prepareHeaders: prepareAuthHeaders,
            })(args, api, extraOptions);
          } else {
            throw new Error('Re-authentication failed for mutation');
          }
        }

        detailedErrorObject = {
          code: (result.error.data as any)?.code || null,
          message: (result.error.data as any)?.message || null,
          data: (result.error.data as any)?.data || null,
          status: result.error.status ?? null,
        };
        throw new Error(
          (result.error.data as any)?.message || 'Mutation API request error occurred',
        );
      }

      return result;
    } catch (err) {
      // User not found in backend
      if ((err as Error).message === 'USER_NOT_FOUND') {
        console.error('User not found');
        window.location.href = NAV_ROUTES.SIGNUP;
        return;
      }
      //Re-authentication error from keyclock
      if ((err as any)?.data?.error === 'Re-authentication failed') {
        prepareAndNavigateForAuthentication();
        return;
      }
      return {
        error: {
          message: 'Failed mutation attempt',
          error: err,
          detailedErrorObject: detailedErrorObject || undefined,
        },
      };
    }
  }
};

const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: authQuery,
  endpoints: (builder) => ({
    authenticate: builder.mutation<AuthResponse, { code: string }>({
      query: (data) => ({
        url: 'token',
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          code: data.code,
          redirect_uri: process.env.REACT_APP_KEYCLOAK_REDIRECT_URI || '',
          client_id: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || '',
          client_secret: process.env.REACT_APP_KEYCLOAK_CLIENT_SECRET || '',
        }).toString(),
      }),
    }),
  }),
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
});

const reAuthApi = createApi({
  reducerPath: 'reAuthApi',
  baseQuery: authQuery,
  endpoints: (builder) => ({
    authenticate: builder.mutation<any, { refresh_token: string }>({
      query: (data) => ({
        url: 'token',
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          refresh_token: data.refresh_token,
          client_id: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || '',
          client_secret: process.env.REACT_APP_KEYCLOAK_CLIENT_SECRET || '',
        }).toString(),
      }),
    }),
  }),
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
});

// Gets new tokens with the refresh token and stores them: the same re-authentication the base
// queries do on a 401. Returns the new access token, or null if there is no refresh token or
// Keycloak rejects it. Used by the activity logger for the event tracker API.
export const refreshAccessToken = async (
  dispatch: (action: any) => any,
): Promise<string | null> => {
  const refreshToken = UserAuth.refreshToken;
  if (!refreshToken) return null;

  try {
    const { access_token, refresh_token } = await dispatch(
      reAuthApi.endpoints.authenticate.initiate({ refresh_token: refreshToken }),
    ).unwrap();
    if (!access_token) return null;

    UserAuth.setTokens({ access_token, refresh_token });
    return access_token;
  } catch {
    return null;
  }
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithRetryAndReAuth as any,
  endpoints: () => ({}),
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  tagTypes: ['user', 'userList', 'modules', 'companyList', 'fundingCaluclator', 'InvestorSplits'],
});

export const noAuthBaseApi = createApi({
  reducerPath: 'noAuthApi',
  baseQuery: noAuthBaseQuery,
  endpoints: () => ({}),
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
});

export const { useAuthenticateMutation } = authApi;

export default baseApi;
